import React, {useState, useEffect, useRef} from "react";
import { motion } from "framer-motion";

// ===== Constants  =====
const BUBBLE_SIZE = "w-3 h-3"; // Tailwind bubble size
const BUBBLE_COLOR = "bg-white"; // Bubble color
const BUBBLE_SHAPE = "rounded-full"; // Bubble shape

const FLOAT_DISTANCE = 200; // How far bubbles float upward
const DRIFT_RANGE = 120; // Total horizontal drift range
const DRIFT_OFFSET = DRIFT_RANGE / 2; // Drift equally left/right

const OPACITY_KEYFRAMES = [0, 1, 0]; // Fade in/out cycle
const SCALE_KEYFRAMES = [0.5, 1.3, 0.2]; // Grow then shrink

const ANIMATION_DURATION = 3.2; // Seconds per bubble animation
const SPAWN_INTERVAL = 400; // Milliseconds between new bubbles
const MAX_DELAY = 3; // Random delay range for staggered animation
const MAX_BUBBLES = 50; // Maximum number of bubbles in memory

const Bubble: React.FC<{ startX: number; startY: number; delay: number }> = ({
                                                                                 startX,
                                                                                 startY,
                                                                                 delay,
                                                                             }) => (
    <motion.div
        className={`absolute ${BUBBLE_SIZE} ${BUBBLE_COLOR} ${BUBBLE_SHAPE}`}
        initial={{
            x: startX,
            y: startY,
            opacity: 0,
            scale: SCALE_KEYFRAMES[0]
        }}
        animate={{
            // Float upward
            y: startY - FLOAT_DISTANCE,
            // Drift sideways randomly
            x: startX + (Math.random() * DRIFT_RANGE - DRIFT_OFFSET),
            opacity: OPACITY_KEYFRAMES,
            scale: SCALE_KEYFRAMES,
        }}
        transition={{
            duration: ANIMATION_DURATION,
            delay,
            ease: "easeInOut"
        }}
    />
);

// ===== Bubble Background Component =====
const BubbleBackground: React.FC<{ count?: number }> = ({ count = 20 }) => {
    const [bubbles, setBubbles] = useState<
        { startX: number; startY: number; delay: number; id: number }[]
    >([]);

    // Use a ref to track the next ID to ensure uniqueness
    const nextId = useRef(0);
    
    useEffect(() => {
        // --- Initialize bubbles on mount ---
        const initialBubbles = Array.from({ length: count }).map((_, i) => ({
            startX: Math.random() * window.innerWidth,
            startY: Math.random() * window.innerHeight,
            delay: Math.random() * MAX_DELAY, // stagger animations
            id: i,
        }));
        setBubbles(initialBubbles);
        nextId.current = count; 

        // --- Continuously spawn new bubbles ---
        const interval = setInterval(() => {
            setBubbles((prev) => [
                ...prev,
                {
                    startX: Math.random() * window.innerWidth,
                    startY: Math.random() * window.innerHeight,
                    delay: 0,
                    id: nextId.current++, // Use and increment the unique ID
                },
                
            ].slice(-MAX_BUBBLES)); // Keep only the latest MAX_BUBBLES
        }, SPAWN_INTERVAL);

        // Cleanup on unmount
        return () => clearInterval(interval);
    }, [count]);

    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {bubbles.map((b) => (
                <Bubble
                    key={b.id}
                    startX={b.startX}
                    startY={b.startY}
                    delay={b.delay}
                />
            ))}
        </div>
    );
};

export default BubbleBackground;
